import { useState, useEffect } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import dayjs from "dayjs";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import { LogItem } from "../../../types/files";
import utilsCommon from "../../../shared/utils/common";
import RevisionTag from "../../../components/RevisionTag";
import { SummaryLogItem } from "../../../types/log";
import useCheckboxGroup from "../../../shared/hooks/useCheckboxGroup";

import WorkgroupPolicyTooltip from "./WorkgroupPolicyTooltip";
import FilesharePolicyTooltip from "./FilesharePolicyTooltip";
import { handleDocPreview } from "../../../shared/utils/api";
import { t } from "i18next";
import classNames from "classnames";
import EmptyFilterPage from "../../../components/EmptyPage/EmptyFilterPage";
import EmptyInitFilterPage from "../../../components/EmptyPage/EmptyInitFilterPage";
import { FULL_FILTER_LOG_BY_TYPE } from ".";
import i18n from "../../../locales/i18n";
import { useNavigate } from "react-router-dom";
import { ChatLogItem } from "../../../types/chat";
import utilsDocs from "../../../shared/utils/docs";
import useWorkgroupStore from "../../../redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "../../../types/workgroup";
import { Trans } from "react-i18next";

export const historyIconByName: {
  [label: string]: { icon: string; name: string; resource: string };
} = {
  webMeeting_add: {
    icon: "zoomNew",
    name: t("log.icon.meetingCreate"),
    resource: "log.newLog.meeting.add",
  },
  webMeeting_change: {
    icon: "calendarTime",
    name: t("log.icon.meetingUpdate"),
    resource: "log.newLog.meeting.edit",
  },
  webMeeting_delete: {
    icon: "ZoomOut",
    name: t("log.icon.meetingCancel"),
    resource: "log.newLog.meeting.delete",
  },
  document_add: {
    icon: "addDoc",
    name: t("log.icon.share"),
    resource: "log.newLog.doc.add",
  },
  document_revision: {
    icon: "edit",
    name: t("log.icon.edit"),
    resource: "log.newLog.doc.revision",
  },
  document_sign: {
    icon: "comment",
    name: t("log.icon.comment"),
    resource: "log.newLog.doc.sign",
  },
  document_change: {
    icon: "edit2",
    name: t("log.icon.changeName"),
    resource: "log.newLog.doc.change",
  },
  document_delete: {
    icon: "delete",
    name: t("log.icon.delete"),
    resource: "log.newLog.doc.delete",
  },
  document_download: {
    icon: "download",
    name: t("log.icon.download"),
    resource: "log.newLog.doc.download",
  },
  document_restore: {
    icon: "redoLeft",
    name: t("log.icon.restore"),
    resource: "log.newLog.doc.restore",
  },
  document_unwrap: {
    icon: "unlock",
    name: t("log.icon.unauthDownload"),
    resource: "log.newLog.doc.unwrap",
  },
  document_view: {
    icon: "preview",
    name: t("log.icon.view"),
    resource: "log.newLog.doc.view",
  },
  document_succession: {
    icon: "fileOwner",
    name: t("log.icon.changeDocMaster"),
    resource: "log.newLog.doc.succession",
  },
  documentRole_add: {
    icon: "checkedShield",
    name: t("log.icon.docChangeAuth"),
    resource: "log.newLog.doc.permission",
  },
  documentRole_delete: {
    icon: "checkedShield",
    name: t("log.icon.docChangeAuth"),
    resource: "log.newLog.doc.permission",
  },
  documentRole_change: {
    icon: "checkedShield",
    name: t("log.icon.docChangeAuth"),
    resource: "log.newLog.doc.permission",
  },
  document_exView: { icon: "", name: "", resource: "" },
  document_link: {
    icon: "link",
    name: t("log.icon.shareLink"),
    resource: "log.newLog.doc.link",
  },
  document_webedit_finish: {
    icon: "edit",
    name: t("log.icon.edit"),
    resource: "log.newLog.doc.revision",
  },
  workgroup_add: {
    icon: "workgroupNew",
    name: t("log.icon.create"),
    resource: "log.newLog.workgroup.add",
  },
  workgroup_change: {
    icon: "setting",
    name: t("log.icon.changeInfo"),
    resource: "log.newLog.workgroup.change",
  },
  workgroupRole_add: {
    icon: "shield",
    name: t("log.icon.workgroupChangeAuth"),
    resource: "log.newLog.workgroup.changePerm",
  },
  workgroupRole_delete: {
    icon: "shield",
    name: t("log.icon.workgroupChangeAuth"),
    resource: "log.newLog.workgroup.changePerm",
  },
  workgroupRole_change: {
    icon: "shield",
    name: t("log.icon.workgroupChangeAuth"),
    resource: "log.newLog.workgroup.changePerm",
  },
  workgroup_succession: {
    icon: "groupOwner",
    name: t("log.icon.changeWorkgroupMaster"),
    resource: "log.newLog.workgroup.changeHead",
  },
  user_change: { icon: "", name: "", resource: "" },
  user_add: {
    icon: "user",
    name: t("log.icon.changeMember"),
    resource: "log.newLog.workgroup.changeMember",
  },
  user_invite: {
    icon: "user",
    name: t("log.icon.changeMember"),
    resource: "log.newLog.workgroup.changeMember",
  },
  user_delete: {
    icon: "user",
    name: t("log.icon.changeMember"),
    resource: "log.newLog.workgroup.changeMember",
  },
  user_withdraw: {
    icon: "user",
    name: t("log.icon.changeMember"),
    resource: "log.newLog.workgroup.changeMember",
  },
  workgroup_delete: {
    icon: "",
    name: "",
    resource: "",
  },
  workgroup_restore: { icon: "", name: "", resource: "" },
  workgroup_deleted: { icon: "", name: "", resource: "" },
  chat_add: {
    icon: "chatLines",
    name: t("log.icon.chat"),
    resource: "log.newLog.chat",
  },
  chat_delete: {
    icon: "chatLines",
    name: t("log.icon.chat"),
    resource: "log.newLog.chat",
  },
};

const historyIconType = [
  ["webMeeting_add"],
  ["webMeeting_change"],
  ["webMeeting_delete"],
  ["document_add"],
  ["document_revision", "document_webedit_finish"],
  ["document_sign"],
  ["document_change"],
  ["document_delete"],
  ["document_download"],
  ["document_restore"],
  ["document_unwrap"],
  ["document_view"],
  ["document_succession"],
  ["documentRole_add", "documentRole_delete", "documentRole_change"],
  ["document_link"],
  ["workgroup_add"],
  ["workgroup_change"],
  ["workgroupRole_add", "workgroupRole_delete", "workgroupRole_change"],
  ["workgroup_succession"],
  ["user_add", "user_invite", "user_delete", "user_withdraw"],
  ["chat_add", "chat_delete"],
];

type DayGroup = SummaryLogItem[][]; // RunGroup[]
type RunGroup = SummaryLogItem[];

const CompressWorkgroupLog = ({
  workgroupId,
  dateFilter,
  docAsideIndex,
  settingButtonInfo,
  pageType,
  changeDocAsideIndex,
  handleInitFilter,
  setSettingInfo,
  handleGetMeetingDetail,
}: {
  workgroupId: string;
  dateFilter?: {
    month: string;
    year: string;
    startMonth: string;
    startYear: string;
    isCustomDate: boolean;
    customFilter: string[];
  };
  docAsideIndex?: string;
  pageType?: "workgroupHome" | "workgroupLog" | "home";
  changeDocAsideIndex?: (idx: string) => void;
  handleInitFilter?: () => void;
  handleGetMeetingDetail: (meetingId: string, occurrenceId?: string) => void;
  settingButtonInfo?: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
  };
  setSettingInfo?: (req: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
  }) => void;
}) => {
  const dir = document.documentElement.dir;

  /** Hooks */
  const toast = useToast();
  const checkboxGroup = useCheckboxGroup();
  const { workgroupState } = useWorkgroupStore();
  const { logState, getSummaryLogList, getLogDetail } = useLogStore();
  const navigate = useNavigate();

  /** State */
  const [logs, changeLogs] = useState<DayGroup[]>();
  const [workgroupInfo, changeWorkgroupInfo] = useState<
    Workgroup | undefined
  >();

  /** fetch */
  useEffect(() => {
    if (pageType === "workgroupLog" && !dateFilter) return;
    const startDate = dateFilter?.startMonth
      ? dateFilter.isCustomDate
        ? dayjs()
            .set("year", parseInt(dateFilter.startYear ?? undefined))
            .set("month", parseInt(dateFilter.startMonth))
            .startOf("month")
            .startOf("day")
            .valueOf()
        : dayjs()
            .set("year", parseInt(dateFilter.startYear ?? undefined))
            .set("month", parseInt(dateFilter.startMonth))
            .startOf("day")
            .valueOf()
      : dayjs().subtract(1, "month").startOf("day").valueOf();
    const endDate = dateFilter?.month
      ? dateFilter.isCustomDate
        ? dayjs()
            .set("year", parseInt(dateFilter.year ?? undefined))
            .set("month", parseInt(dateFilter?.month ?? undefined))
            .endOf("month")
            .endOf("day")
            .valueOf()
        : dayjs()
            .set("year", parseInt(dateFilter.year ?? undefined))
            .set("month", parseInt(dateFilter?.month ?? undefined))
            .endOf("day")
            // .endOf("month")
            .valueOf()
      : dayjs().endOf("day").valueOf();
    getSummaryLogList({
      workgroupId,
      startDate: startDate + "",
      endDate: endDate + "",
      filterDocument: dateFilter?.customFilter.includes("file")
        ? FULL_FILTER_LOG_BY_TYPE.filterDocument
        : [],
      filterWebMeeting: dateFilter?.customFilter.includes("meeting")
        ? FULL_FILTER_LOG_BY_TYPE.filterWebMeeting
        : [],
      filterWorkgroup: dateFilter?.customFilter.includes("workgroup")
        ? FULL_FILTER_LOG_BY_TYPE.filterWorkgroup
        : [],
      filterChat: dateFilter?.customFilter.includes("chat")
        ? FULL_FILTER_LOG_BY_TYPE.filterChat
        : [],
    });
    checkboxGroup.handleAllClick([]);
  }, [dateFilter, workgroupId]);
  useEffect(() => {
    searchCompressedLog();
    document.getElementById("compressedLog")?.scrollTo(0, 0);
  }, [logState.summaryLogList]);
  useEffect(() => {
    changeWorkgroupInfo(
      workgroupState.workgroupSidemenuList?.filter(
        (item: Workgroup) => item.workgroupId === workgroupId
      )[0]
    );
  }, [workgroupState.workgroupSidemenuList, workgroupId]);
  /** 스크롤 차단(더보기 열렸을 때) */
  useEffect(() => {
    const target = document.getElementById("compressedLog");
    const target1 = document.getElementById("workgroupHome");
    const target2 = document.getElementById("home");
    if (!target && !target1 && !target2) return;

    const handleScroll = (e: Event) => {
      if (settingButtonInfo?.log) e.preventDefault();
    };
    if (target) {
      target.addEventListener("wheel", handleScroll, { passive: false });
      target.addEventListener("touchmove", handleScroll, { passive: false });
    }
    if (target1) {
      target1.addEventListener("wheel", handleScroll, { passive: false });
      target1.addEventListener("touchmove", handleScroll, { passive: false });
    }
    if (target2) {
      target2.addEventListener("wheel", handleScroll, { passive: false });
      target2.addEventListener("touchmove", handleScroll, { passive: false });
    }
    return () => {
      target?.removeEventListener("wheel", handleScroll);
      target?.removeEventListener("touchmove", handleScroll);
      target1?.removeEventListener("wheel", handleScroll);
      target1?.removeEventListener("touchmove", handleScroll);
      target2?.removeEventListener("wheel", handleScroll);
      target2?.removeEventListener("touchmove", handleScroll);
    };
  }, [settingButtonInfo]);

  /** 사용자 셋 키 */
  const getInvolvedUsersKey = (log: SummaryLogItem) => {
    const ids = (log.involvedUsers ?? [])
      .map((u) => u.userId || u.userName || "")
      .sort();
    return ids.join(",");
  };

  /** 날짜 × 사용자-run 그룹핑 */
  const searchCompressedLog = () => {
    const byDayAndRun: DayGroup[] = [];
    let curDay = "";
    let curRunKey = "";

    logState.summaryLogList?.forEach((item: SummaryLogItem) => {
      const dayKey = utilsCommon.renderDateFormat(item.firstDate, "YYYY-MM-DD");
      const runKey = `${dayKey}||${getInvolvedUsersKey(item)}`;

      if (dayKey !== curDay) {
        byDayAndRun.push([]);
        curDay = dayKey;
        curRunKey = "";
      }
      const dayGroup = byDayAndRun[byDayAndRun.length - 1];

      if (runKey !== curRunKey) {
        dayGroup.push([]);
        curRunKey = runKey;
      }
      const runGroup = dayGroup[dayGroup.length - 1];
      runGroup.push(item);
    });

    changeLogs(byDayAndRun);
  };

  /** 채팅: 줄바꿈 줄이기 */
  const removeEnterTitle = (title?: string) =>
    title && title.includes("\n") ? title.split("\n")[0] + "···" : title;

  /** 채팅 아이템 렌더 */
  const renderChatLogItem = (chat?: ChatLogItem, isClicked?: boolean) =>
    chat?.attachments && chat?.attachments.length > 0 ? (
      <Box
        alignItems="center"
        maxWidth={
          chat.parentMessage ? "calc(100% - 60px)" : "calc(100% - 40px)"
        }
      >
        <Box minWidth={20}>
          {utilsDocs.renderDocIcon(chat?.attachments[0].title, 20)}
        </Box>

        <Tooltip
          wordBreak
          message={chat?.attachments[0].title}
          classes="mg ml-4"
          maxWidth={"calc(100% - 24px)"}
        >
          <Typography
            type={isClicked ? "subtitle2" : "body2_rg"}
            onClick={() =>
              chat?.messageId &&
              navigate(
                utilsCommon.renderProperNavigatePath(
                  `/workgroup/${workgroupId}/chat?chatId=${chat?.messageId}`
                )
              )
            }
            ellipsis
          >
            {chat?.attachments[0].title ?? t("common.chat")}
          </Typography>
        </Tooltip>
      </Box>
    ) : (
      <Tooltip
        wordBreak
        message={chat?.body}
        maxWidth={
          chat?.parentMessage ? "calc(100% - 60px)" : "calc(100% - 40px)"
        }
        hideToolTip={!chat?.body}
      >
        <Typography
          type={isClicked ? "subtitle2" : "body2_rg"}
          onClick={() =>
            chat?.messageId &&
            navigate(
              utilsCommon.renderProperNavigatePath(
                `/workgroup/${workgroupId}/chat?chatId=${chat?.messageId}`
              )
            )
          }
          ellipsis
        >
          {chat?.body ? removeEnterTitle(chat.body) : t("common.chat")}
        </Typography>
      </Tooltip>
    );

  /** 타입별 본문 */
  const renderChatLog = (
    log: SummaryLogItem | LogItem,
    index: string,
    hasChildren: boolean,
    isSingle?: boolean
  ) => {
    const chat =
      "logDetailInfo" in log && log.logDetailInfo?.chatLogData
        ? (JSON.parse(log.logDetailInfo?.chatLogData)[0] as ChatLogItem)
        : "chatLogData" in log && log?.chatLogData
        ? (JSON.parse(log?.chatLogData)[0] as ChatLogItem)
        : undefined;

    return chat?.body ? (
      <Box
        classes="chatLog"
        style={{ flex: 1, minWidth: 0, marginTop: isSingle ? "10px" : "20px" }}
        alignItems="flex-start"
        direction="column"
      >
        {chat?.parentMessage && (
          <Tooltip
            wordBreak
            message={
              chat?.parentMessage
                ? chat.parentMessage?.attachments[0]?.title ??
                  chat.parentMessage?.body
                : "-"
            }
            classes="mg mb-8"
          >
            <Typography
              type="body2_rg"
              ellipsis
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {chat?.parentMessage
                ? removeEnterTitle(chat.parentMessage?.attachments[0]?.title) ??
                  removeEnterTitle(chat.parentMessage?.body)
                : "-"}
            </Typography>
          </Tooltip>
        )}
        <Box alignItems="center" width={"100%"}>
          {chat?.parentMessage && (
            <Icons label="downRight" variant="line" classes="mg mr-8" />
          )}
          {renderChatLogItem(
            chat,
            checkboxGroup.isSelected(index) && hasChildren
          )}
        </Box>
      </Box>
    ) : (
      <Box alignItems="center" width={"100%"} classes="chatLog">
        {renderChatLogItem(chat)}
      </Box>
    );
  };

  const renderLogAfter = (
    log: SummaryLogItem | LogItem,
    isTooltip?: boolean
  ) => {
    let resource =
      historyIconByName[log.logType + "_" + log.actionType].resource;
    const documentDiv = (
      <>
        {!isTooltip && (
          <Box minWidth={24}>
            {utilsDocs.renderDocIcon(
              "documentName" in log
                ? log?.documentName
                : "documnetName" in log
                ? log.documnetName
                : "",
              24,
              "normal",
              {
                marginBottom: window.eco.isCspp ? "" : "-6px",
              }
            )}
          </Box>
        )}
        {isTooltip ? (
          "documentName" in log ? (
            log?.documentName
          ) : "documnetName" in log ? (
            log.documnetName
          ) : (
            ""
          )
        ) : (
          <Typography
            ellipsis={!isTooltip}
            exactColor={isTooltip ? "white" : undefined}
          >
            {"documentName" in log
              ? log?.documentName
              : "documnetName" in log
              ? log.documnetName
              : ""}
          </Typography>
        )}
        {!isTooltip && (
          <RevisionTag
            style={{ display: "inline-flex", alignSelf: "center" }}
            revisionNo={log.revisionNo}
            classes={classNames({
              "mg ml-4 mr-6": i18n.language.includes("ko"),
            })}
          />
        )}
      </>
    );
    let topic = "";
    try {
      topic = log.logData
        ? JSON.parse(log.logData)?.topic ?? ""
        : "desc" in log
        ? log.desc
        : "";
    } catch (e) {
      topic = "?";
    }
    const secondDiv = log.logType.includes("webMeeting") ? (
      <>
        {!isTooltip && (
          <Icons
            variant="line"
            label="zoom"
            size="16"
            classes="mg ml-4 mr-2"
            // style={{ marginBottom: "-4px" }}
          />
        )}
        {topic}
      </>
    ) : log.logType.includes("workgroup") ? (
      <> {typeof log.logData === "string" ? log.logData : ""}</>
    ) : (
      documentDiv
    );
    return resource === "" ? (
      log.actionType + ""
    ) : (
      <Trans
        i18nKey={resource}
        values={{
          user:
            "userId" in log
              ? utilsCommon.getUserNameIdTag(log.userId, log.userName)
              : utilsCommon.getUserNameIdTag(
                  log.involvedUsers[0]?.userId,
                  log.involvedUsers[0]?.userName
                ),
        }}
        components={{
          1: isTooltip ? (
            <></>
          ) : (
            <Typography
              type="subtitle2"
              style={{ display: "inline-flex" }}
              lineHeight={"24px"}
              whiteSpace="pre"
              classes={classNames("mg ml-2", {
                "mr-2": !i18n.language.includes("ko"),
              })}
            />
          ),
          2: secondDiv ?? <></>,
          3: (
            <Typography
              classes={classNames({
                "hover-text-underline":
                  workgroupInfo?.workgroupType !== "HUB" && log.syncId,
              })}
              style={{ display: "inline-block" }}
              onClick={(e: any) => {
                e.stopPropagation();
                log.syncId &&
                  workgroupInfo?.workgroupType !== "HUB" &&
                  handleDocPreview({
                    permission: "view",
                    workgroupId,
                    syncId: log.syncId,
                    toast,
                    t,
                    revisionNo: log?.revisionNo,
                  });
              }}
            />
          ),
        }}
      />
    );
  };
  const renderTitleTooltip = (
    log: SummaryLogItem | LogItem | SummaryLogItem[]
  ) => {
    if (!Array.isArray(log)) return;
    let topic = "";
    try {
      topic =
        "desc" in log[0]
          ? log[0].desc
          : log[0].logData
          ? JSON.parse(log[0].logData).topic
          : "";
    } catch (err) {
      topic = log[0].logData ?? "";
    }

    const dataSource = getActionTypeCounts(sumActionTypeCounts(log));
    const merged = historyIconType
      .map((grp) => {
        const found = dataSource.filter((ds) => grp.includes(ds.key));
        return found.length
          ? { ...found[0], count: found.reduce((s, it) => s + it.count, 0) }
          : undefined;
      })
      .filter(Boolean) as { key: string; count: number }[];
    const targetList = merged.map((item) => {
      if (item.key.includes("document_") || item.key.includes("documentRole_"))
        return (
          "DOCUMENT_" +
          ("documentName" in item
            ? (item as any)?.documentName
            : "documnetName" in item
            ? (item as any).documnetName
            : "")
        );
      else if (item.key.includes("chat")) return "CHAT";
      else if (item.key.includes("webMeeting_")) return `MEETING_${topic}`;
      else return "WORKGROUP";
    });
    return [
      targetList.filter((target) => target.startsWith("DOCUMENT_")).length > 0
        ? "DOCUMENT"
        : "",
      targetList.filter((target) => target.startsWith("WORKGROUP")).length > 0
        ? "WORKGROUP"
        : "",
      targetList.filter((target) => target.startsWith("CHAT")).length > 0
        ? "CHAT"
        : "",
      targetList.filter((target) => target.startsWith("MEETING_")).length > 0
        ? "MEETING"
        : "",
    ]
      .filter((target) => target !== "")
      .map((item) =>
        item.includes("DOCUMENT")
          ? Array.from(
              new Set(
                log
                  .filter((item) => item.logType.startsWith("document"))
                  .map((item) => item.syncId)
              )
            ).length === 1
            ? log.filter((item) => item.logType.startsWith("document"))[0]
                .documentName
            : t("log.count.file", {
                doc:
                  log?.filter((item) => item.logType.startsWith("document"))[0]
                    ?.documentName ?? "",
                count:
                  Array.from(
                    new Set(
                      log
                        .filter((item) => item.logType.startsWith("document"))
                        .map((item) => item.syncId)
                    )
                  ).length - 1,
              })
          : item.includes("CHAT")
          ? t("log.count.chat", {
              count: merged
                .filter((item) => item.key.startsWith("chat"))
                .reduce((acc, cur) => acc + cur.count, 0),
            })
          : item.includes("WORKGROUP")
          ? t("log.count.workgroup", {
              count: merged
                .filter(
                  (item) =>
                    item.key.startsWith("workgroup") ||
                    item.key.startsWith("user")
                )
                .reduce((acc, cur) => acc + cur.count, 0),
            })
          : item.includes("MEETING")
          ? Array.from(
              new Set(
                log
                  .filter((item) => item.logType.startsWith("webMeeting"))
                  .map((item) => item.logDetailInfo?.syncId)
              )
            ).length === 1
            ? JSON.parse(
                log.filter((item) => item.logType.startsWith("webMeeting"))[0]
                  .logData ?? ""
              ).topic
            : t("log.count.meeting", {
                topic: JSON.parse(
                  log.filter((item) => item.logType.startsWith("webMeeting"))[0]
                    .logData ?? ""
                ).topic,
                count:
                  Array.from(
                    new Set(
                      log
                        .filter((item) => item.logType.startsWith("webMeeting"))
                        .map((item) => item.logDetailInfo?.syncId)
                    )
                  ).length - 1,
              })
          : log[0].logType
      )
      .join(", ");
  };
  /** 본문 타이틀/묶음 문자열 */
  const renderLogDetailStr = (
    log: SummaryLogItem | LogItem | SummaryLogItem[],
    index: string,
    hasChildren: boolean
  ) => {
    if (
      Array.isArray(log) &&
      (log.length > 1 || (log.length === 1 && hasChildren))
    ) {
      let topic = "";
      try {
        topic =
          "desc" in log[0]
            ? log[0].desc
            : log[0].logData
            ? JSON.parse(log[0].logData).topic
            : "";
      } catch (err) {
        topic = log[0].logData ?? "";
      }

      const dataSource = getActionTypeCounts(sumActionTypeCounts(log));
      const merged = historyIconType
        .map((grp) => {
          const found = dataSource.filter((ds) => grp.includes(ds.key));
          return found.length
            ? { ...found[0], count: found.reduce((s, it) => s + it.count, 0) }
            : undefined;
        })
        .filter(Boolean) as { key: string; count: number }[];
      const targetList = merged.map((item) => {
        if (
          item.key.includes("document_") ||
          item.key.includes("documentRole_")
        )
          return (
            "DOCUMENT_" +
            ("documentName" in item
              ? (item as any)?.documentName
              : "documnetName" in item
              ? (item as any).documnetName
              : "")
          );
        else if (item.key.includes("chat")) return "CHAT";
        else if (item.key.includes("webMeeting_")) return `MEETING_${topic}`;
        else return "WORKGROUP";
      });
      return [
        targetList.filter((target) => target.startsWith("DOCUMENT_")).length > 0
          ? "DOCUMENT"
          : "",
        targetList.filter((target) => target.startsWith("WORKGROUP")).length > 0
          ? "WORKGROUP"
          : "",
        targetList.filter((target) => target.startsWith("CHAT")).length > 0
          ? "CHAT"
          : "",
        targetList.filter((target) => target.startsWith("MEETING_")).length > 0
          ? "MEETING"
          : "",
      ]
        .filter((target) => target !== "")
        .map((item) => (
          <Box alignItems="center" style={{ overflow: "hidden" }}>
            {item.includes("DOCUMENT") && (
              <Box minWidth={20}>
                {utilsDocs.renderDocIcon(
                  log.filter((item) => item.logType.startsWith("document"))[0]
                    .documentName,
                  20
                )}
              </Box>
            )}
            <Typography type={"subtitle2"} ellipsis>
              {item.includes("DOCUMENT")
                ? Array.from(
                    new Set(
                      log
                        .filter((item) => item.logType.startsWith("document"))
                        .map((item) => item.syncId)
                    )
                  ).length === 1
                  ? log.filter((item) => item.logType.startsWith("document"))[0]
                      .documentName
                  : t("log.count.file", {
                      doc:
                        log?.filter((item) =>
                          item.logType.startsWith("document")
                        )[0]?.documentName ?? "",
                      count:
                        Array.from(
                          new Set(
                            log
                              .filter((item) =>
                                item.logType.startsWith("document")
                              )
                              .map((item) => item.syncId)
                          )
                        ).length - 1,
                    })
                : item.includes("CHAT")
                ? t("log.count.chat", {
                    count: merged
                      .filter((item) => item.key.startsWith("chat"))
                      .reduce((acc, cur) => acc + cur.count, 0),
                  })
                : item.includes("WORKGROUP")
                ? t("log.count.workgroup", {
                    count: merged
                      .filter(
                        (item) =>
                          item.key.startsWith("workgroup") ||
                          item.key.startsWith("user")
                      )
                      .reduce((acc, cur) => acc + cur.count, 0),
                  })
                : item.includes("MEETING")
                ? Array.from(
                    new Set(
                      log
                        .filter((item) => item.logType.startsWith("webMeeting"))
                        .map((item) => item.logDetailInfo?.syncId)
                    )
                  ).length === 1
                  ? t("common.meeting") +
                    JSON.parse(
                      log.filter((item) =>
                        item.logType.startsWith("webMeeting")
                      )[0].logData ?? ""
                    ).topic +
                    "'"
                  : t("log.count.meeting", {
                      topic: JSON.parse(
                        log.filter((item) =>
                          item.logType.startsWith("webMeeting")
                        )[0].logData ?? ""
                      ).topic,
                      count:
                        Array.from(
                          new Set(
                            log
                              .filter((item) =>
                                item.logType.startsWith("webMeeting")
                              )
                              .map((item) => item.logDetailInfo?.syncId)
                          )
                        ).length - 1,
                    })
                : log[0].logType}
            </Typography>
            <Box
              style={{ margin: "0 10px", borderRight: "1px solid #B5BBC2" }}
              height={20}
              width={5}
            />
          </Box>
        ));
    }

    return ("logType" in log && log.logType === "chat") ||
      (Array.isArray(log) && log.length === 1 && log[0].logType === "chat") ? (
      <Box direction="column" maxWidth={"100%"}>
        <Tooltip
          wordBreak
          message={renderLogAfter(Array.isArray(log) ? log[0] : log, true)}
        >
          <Box
            classes="log_inside"
            style={{ textOverflow: "ellipsis", overflow: "hidden" }}
          >
            <Typography
              whiteSpace="pre"
              ellipsis
              style={{
                display: "inline-block",
                // height: 56,
                alignContent: "center",
                alignItems: "center",
              }}
            >
              {renderLogAfter(Array.isArray(log) ? log[0] : log)}
            </Typography>
          </Box>
        </Tooltip>
        {renderChatLog(
          Array.isArray(log) ? log[0] : log,
          index,
          hasChildren,
          Array.isArray(log)
        )}
      </Box>
    ) : Array.isArray(log) && log.length === 1 ? (
      <Box
        // maxWidth={
        //   pageType !== "workgroupLog"
        //     ? "calc(100% - 200px)"
        //     : "calc(100% - 250px)"
        // }
        maxWidth={"100%"}
        alignItems="center"
      >
        <Tooltip wordBreak message={renderLogAfter(log[0], true)}>
          <Box
            classes="log_inside"
            alignItems="center"
            style={{ textOverflow: "ellipsis", overflow: "hidden" }}
          >
            <Typography
              whiteSpace="pre"
              ellipsis
              lineHeight={"24px"}
              style={{
                display: "flex",
                // height: 56,
                alignContent: "center",
                alignItems: "center",
              }}
            >
              {renderLogAfter(log[0])}
            </Typography>
          </Box>
        </Tooltip>
        {renderLogDetailLongStr(log[0]) && (
          <Tooltip
            wordBreak
            message={renderLogDetailLongStr(log[0])}
            classes="logTooptip"
          >
            <Icons variant="line" label="info" />
          </Tooltip>
        )}
      </Box>
    ) : !Array.isArray(log) ? (
      <Box maxWidth={"100%"} alignItems="center">
        <Tooltip wordBreak message={renderLogAfter(log, true)}>
          <Box
            classes="log_inside"
            alignItems="center"
            style={{ textOverflow: "ellipsis", overflow: "hidden" }}
          >
            <Typography
              // whiteSpace="pre"
              ellipsis
              as="div"
              lineHeight={"24px"}
              style={{
                display: "flex",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              {renderLogAfter(log)}
            </Typography>
          </Box>
        </Tooltip>
        {renderLogDetailLongStr(log) && (
          <Tooltip
            wordBreak
            classes="logTooptip"
            message={renderLogDetailLongStr(log)}
          >
            <Icons variant="line" label="info" />
          </Tooltip>
        )}
      </Box>
    ) : (
      log.length
    );
  };
  /** 툴팁 내용(정책 등) */
  const renderLogDetailLongStr = (log: SummaryLogItem | LogItem) => {
    const detailLog = "logDetailInfo" in log ? log.logDetailInfo : log;
    if (detailLog?.historyType === 2) {
      switch (detailLog.saveType) {
        case 1:
          return detailLog.policyLogs &&
            detailLog.policyLogs[0]?.permissionPolicy?.policy
              ?.fileShareLinkPolicy ? (
            <FilesharePolicyTooltip
              linkPolicy={
                detailLog.policyLogs[0].permissionPolicy.policy
                  ?.fileShareLinkPolicy
              }
              logDate={detailLog.logDate}
            />
          ) : (
            detailLog?.desc ??
              (detailLog?.logDatas && !!detailLog?.logDatas[0]
                ? detailLog?.logDatas[0] ?? ""
                : `${t("common.workgroupName")}: ` + detailLog.workgroupName)
          );
        case 2:
          return detailLog.policyLogs && detailLog.policyLogs[0] ? (
            <WorkgroupPolicyTooltip
              logItem={detailLog.policyLogs}
              title={t("workgroup.workgroupDefaultPermssion")}
            />
          ) : (
            t("common.workgroup")
          );
        case 3:
          return detailLog.desc;
        case 4:
          return (
            (["add", "invite"].includes(detailLog.actionType)
              ? t("log.detailLog.inviteUser")
              : t("log.detailLog.excludeUser")) + detailLog.logDatas.join(", ")
          );
        default:
          return t("common.workgroup");
      }
    } else if (detailLog?.historyType === 1) {
      switch (detailLog.saveType) {
        case 2:
          return detailLog.desc;
        case 3:
          return t("log.detailLog.pastFileName") + detailLog.desc;
        case 8:
          return detailLog.desc;
        case 0:
          return detailLog.policyLogs ? (
            <WorkgroupPolicyTooltip logItem={detailLog.policyLogs} />
          ) : (
            ""
          );
        case 9:
          return detailLog.policyLogs ? (
            <WorkgroupPolicyTooltip logItem={detailLog.policyLogs} />
          ) : (
            t("file.policy.none")
          );
        case 10:
          return detailLog.policyLogs &&
            detailLog.policyLogs[0] &&
            detailLog.policyLogs[0]?.permissionPolicy?.policy
              ?.fileShareLinkPolicy ? (
            <FilesharePolicyTooltip
              linkPolicy={
                detailLog.policyLogs[0].permissionPolicy.policy
                  ?.fileShareLinkPolicy
              }
              logDate={detailLog?.logDate}
            />
          ) : (
            detailLog.documnetName
          );
        default:
          return "";
      }
    }
    return "";
  };
  const sameKey = (a: LogItem, b: LogItem) =>
    a?.syncId === b?.syncId &&
    a?.userId === b?.userId &&
    a?.userName === b?.userName &&
    a?.revisionNo === b?.revisionNo &&
    a?.historyType === 1 &&
    b?.historyType === 1;
  const mergeSaveType0With9 = (logs: LogItem[]) => {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    const result = [];

    for (const curr of logs) {
      const prev = result[result.length - 1];

      // consecutive(연속)한 prev, curr이 merge 조건이면 prev에 합치기
      if (
        prev &&
        sameKey(prev, curr) &&
        prev.saveType === 0 &&
        curr.saveType === 9
      ) {
        // prev를 기준으로 합치고, policyLogs는 curr로 덮어쓰기
        prev.policyLogs = curr.policyLogs;
        continue; // curr은 결과에 추가하지 않음(= merged)
      }

      // 기본: 그냥 추가 (원본 객체가 바뀌는 게 싫으면 structuredClone/스프레드 사용)
      result.push(curr);
    }
    return result;
  };
  /** 펼치기(클릭): 해당 run의 대표 summary만 상세 조회 */
  const handleGetLogBelow = (id: string) => {
    const parts = id.split("--"); // YYYY-MM-DD -- runIdx [-- itemIdx]
    const [dateKey, runIdxStr, itemIdxStr] = parts;
    const runIdx = parseInt(runIdxStr ?? "", 10);
    const itemIdx = parseInt(itemIdxStr ?? "0", 10); // ← 핵심

    checkboxGroup.handleClick(id);
    if (checkboxGroup.isSelected(id) || Number.isNaN(runIdx)) return;

    const dayIdx =
      logs?.findIndex(
        (day) =>
          utilsCommon.renderDateFormat(day[0][0].firstDate, "YYYY-MM-DD") ===
          dateKey
      ) ?? -1;
    if (dayIdx < 0) return;

    const run = logs![dayIdx][runIdx];
    if (!run || run.length === 0) return;

    // 클릭한 run 내부 item을 정확히 조회
    getLogDetail({
      logIdList: [...run.map((item) => item.logIds)].flat(),
    }).then((resp: any) => {
      let detailLogs: LogItem[] = resp.payload?.logResults ?? [];
      detailLogs = detailLogs.sort((a, b) => b.logDate - a.logDate);

      changeLogs((prev) => {
        if (!prev) return prev;
        const cloned = prev.map((day) => day.map((r) => [...r]));
        // 클릭한 item에 childrenLogs 저장 (run[0] 고정 X)
        cloned[dayIdx][runIdx][itemIdx] = {
          ...cloned[dayIdx][runIdx][itemIdx],
          childrenLogs: mergeSaveType0With9(detailLogs),
        };

        return cloned;
      });
    });
  };

  /** 시간 범위 표기 (버그 수정) */
  const renderCompressedDateRange = (
    hasChildren: boolean,
    startLog: number,
    endLog?: number
  ) => {
    if (!hasChildren) return utilsCommon.renderDateFormat(startLog, "h:mm A");

    const startTime = utilsCommon.renderDateFormat(startLog, "h:mm A");
    const endTime = utilsCommon.renderDateFormat(endLog!, "h:mm A");

    if (startTime === endTime) return startTime;

    const startIsPM = dayjs(startLog).hour() >= 12;
    const endIsPM = dayjs(endLog!).hour() >= 12; // ← endLog 기준으로 수정
    if (startIsPM === endIsPM) {
      return utilsCommon.renderDateFormat(startLog, "h:mm") + "-" + endTime;
    }
    return startTime + "-" + endTime;
  };

  /** 아이콘 카운트 합산(run 단위) */
  const sumActionTypeCounts = (logArray: SummaryLogItem[]) => {
    const acc: Record<string, number> = {};

    // involvedUsers + syncId + revisionNo를 묶어서 "룰 적용 대상"을 특정
    const getInvolvedUsersKey = (log: SummaryLogItem) => {
      const ids = (log.involvedUsers ?? [])
        .map((u) => u.userId || u.userName || "")
        .sort();
      return ids.join(",");
    };

    const makeKey = (log: SummaryLogItem) =>
      `${log.syncId}__${getInvolvedUsersKey(log)}__${log.revisionNo}`;

    // 1) "룰 트리거" 되는 키 수집: document_add와 documentRole_add가 같은 summary에 같이 있는 경우
    const dropRoleAddKeys = new Set<string>();
    logArray.forEach((l) => {
      const counts = l.actionTypeCounts || {};
      if (
        (counts["document_add"] ?? 0) > 0 &&
        (counts["documentRole_add"] ?? 0) > 0
      ) {
        dropRoleAddKeys.add(makeKey(l));
      }
    });

    // 2) 합산 (룰 대상이면 documentRole_add만 제외)
    logArray.forEach((l) => {
      const counts = l.actionTypeCounts || {};
      const shouldDropRoleAdd = dropRoleAddKeys.has(makeKey(l));

      Object.entries(counts).forEach(([k, v]) => {
        if (v == null) return;
        if (shouldDropRoleAdd && k === "documentRole_add") return;

        acc[k] = (acc[k] ?? 0) + v;
      });
    });
    return acc;
  };

  const getActionTypeCounts = (
    data: LogItem[] | Record<string, number | undefined>
  ) => {
    if (Array.isArray(data)) {
      const uniqueKeys = data.filter(
        (v, i, a) =>
          i ===
          a.findIndex(
            (x) =>
              `${x.logType}_${x.actionType}` === `${v.logType}_${v.actionType}`
          )
      );
      return uniqueKeys.map((item) => {
        const key = `${item.logType}_${item.actionType}`;
        const count =
          data.filter((x) => `${x.logType}_${x.actionType}` === key).length ??
          0;
        return { key, count };
      });
    } else {
      return Object.entries(data)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => ({ key, count: value ?? 0 }));
    }
  };

  const renderLogIcon = (
    data: LogItem[] | Record<string, number | undefined>,
    isInBody?: boolean
  ) => {
    const dataSource = getActionTypeCounts(data);
    const merged = historyIconType
      .map((grp) => {
        const found = dataSource.filter((ds) => grp.includes(ds.key));
        return found.length
          ? { ...found[0], count: found.reduce((s, it) => s + it.count, 0) }
          : undefined;
      })
      .filter(Boolean) as { key: string; count: number }[];

    return (
      <Tooltip
        wordBreak
        message={merged
          .map((it) => `${historyIconByName[it.key].name}: ${it.count}`)
          .join(", ")}
        display="flex"
        classes="iconList"
      >
        {merged
          .filter((_, index) => {
            const wide =
              window.innerWidth >
              (window.location.href.includes("/home") ? 2000 : 1540);
            return (index > 3 && wide) || index < 3 || !isInBody || wide;
          })
          .map((it) => (
            <Box alignItems="center" key={`icon-${it.key}`}>
              <Icons
                label={historyIconByName[it.key].icon}
                variant="line"
                size="16"
                isOn
              />
              <Typography type="body2_md" classes="mg ml-6">
                {it.count}
              </Typography>
            </Box>
          ))}
        {isInBody &&
          merged.length > 3 &&
          (window.innerWidth < 1540 ||
            window.location.href.includes("/home")) && (
            <Typography style={{ marginInlineStart: 0 }} type="subtitle2">
              {"..."}
            </Typography>
          )}
      </Tooltip>
    );
  };

  /** 단일 줄 렌더 (run/summary/logitem 통합) */
  const renderLogEachItem = (
    received: SummaryLogItem | LogItem | SummaryLogItem[],
    id: string,
    depth: number,
    isLast?: boolean
  ) => {
    const log = Array.isArray(received) ? received[0] : received;
    const hasNoChildren =
      (Array.isArray(received) &&
        received.length <= 1 &&
        (received[0]?.logIds?.length ?? 0) <= 1) ||
      (!Array.isArray(received) &&
        "children" in received &&
        (!received.children || received.children?.length <= 1)) ||
      (!Array.isArray(received) &&
        "logIds" in received &&
        received.logIds?.length <= 1) ||
      (!Array.isArray(received) &&
        !("children" in received) &&
        !("logIds" in received));

    const chat =
      "logDetailInfo" in log && log.logDetailInfo?.chatLogData
        ? (JSON.parse(log.logDetailInfo?.chatLogData)[0] as ChatLogItem)
        : "chatLogData" in log && (log as any)?.chatLogData
        ? (JSON.parse((log as any)?.chatLogData)[0] as ChatLogItem)
        : undefined;

    const logId =
      "logIds" in log ? log.logIds[0] : "logId" in log ? log.logId : id;
    const newDepth =
      "involvedUsers" in log && hasNoChildren && depth > 1 ? 2 : depth;
    return (
      <Box
        height={chat ? undefined : 40}
        style={{ overflow: "hidden" }}
        classes={classNames(`compressed-item--depth${newDepth}`, {
          selected:
            (docAsideIndex === logId && hasNoChildren) ||
            settingButtonInfo?.logId === ("logIds" in log ? log.logIds[0] : id),
          isOpen: checkboxGroup.isSelected(id) && newDepth <= 2,
          isLast: isLast,
        })}
        onClick={() => {
          if (
            hasNoChildren &&
            changeDocAsideIndex &&
            workgroupInfo?.workgroupType !== "HUB"
          ) {
            changeDocAsideIndex(logId);
          }
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          e.preventDefault();
          hasNoChildren &&
            workgroupInfo?.workgroupType !== "HUB" &&
            // "logIds" in log &&
            log?.logType?.includes("document") &&
            setSettingInfo &&
            setSettingInfo({
              x: dir === "ltr" ? e.pageX - 250 : e.pageX,
              y: e.pageY - 61,
              logId:
                settingButtonInfo?.logId === logId
                  ? settingButtonInfo.logId
                  : "logIds" in log
                  ? log.logIds[0]
                  : "logId" in log
                  ? log.logId
                  : id,
              log:
                settingButtonInfo?.logId === logId
                  ? settingButtonInfo.log
                  : undefined,
            });
        }}
      >
        {!hasNoChildren ? (
          <Button
            btnStyle="table-icon"
            size="sm"
            width={32}
            onClick={(e) => {
              e.stopPropagation();
              handleGetLogBelow(id);
            }}
            leadingIcon={
              <Icons
                variant="line"
                label="chevron"
                direction={checkboxGroup.isSelected(id) ? "down" : "right"}
              />
            }
          />
        ) : (
          <Box width={32} height={32} />
        )}
        {"involvedUsers" in log && (!hasNoChildren || newDepth === 1) ? (
          <Box
            minWidth={pageType === "workgroupLog" ? 180 : 120}
            width={pageType === "workgroupLog" ? 180 : 120}
            // minWidth={180}
            // width={180}
            alignItems="center"
            classes="mg ml-8 mr-20"
          >
            <Tooltip
              wordBreak
              style={{ display: "flex" }}
              message={log.involvedUsers
                .map((item) =>
                  utilsCommon.getUserNameIdTag(item.userId, item.userName)
                )
                .join(", ")}
              maxWidth={pageType === "workgroupLog" ? 180 : 120}
              // maxWidth={180}
            >
              <Box
                alignItems="center"
                width={"fit-content"}
                maxWidth={"100%"}
                style={{ overflow: "hidden" }}
              >
                <Typography
                  type={"body2_rg"}
                  classes="compressed-target"
                  ellipsis
                  maxWidth={pageType === "workgroupLog" ? 180 : 120}
                  // maxWidth={180}
                >
                  {utilsCommon.getUserNameIdTag(
                    log.involvedUsers[0].userId,
                    log.involvedUsers[0].userName,
                    pageType !== "workgroupLog"
                  ) +
                    (log.involvedUsers.length > 1
                      ? ` ${t("common.andNPeople", {
                          num: log.involvedUsers.length - 1,
                        })}`
                      : "")}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        ) : (
          <></>
        )}
        <Box
          maxWidth={
            newDepth === 1
              ? pageType === "workgroupLog"
                ? "calc(100% - 236px)"
                : "calc(100% - 176px)"
              : "calc(100% - 32px)"
          }
        >
          <Tooltip
            wordBreak
            classes="alignContent-center overflow-hidden"
            display="flex"
            style={{ alignContent: "center" }}
            hideToolTip={
              !Array.isArray(received) ||
              received.length === 1 ||
              ("historyType" in received && depth === 1)
            }
            message={
              Array.isArray(received) ? renderTitleTooltip(received) : []
            }
          >
            <Box
              alignItems="center"
              classes="ellipsis"
              maxWidth={"100%"}
              style={{ flex: "1 0 auto" }}
            >
              {renderLogDetailStr(received, id, !hasNoChildren)}
            </Box>
          </Tooltip>
          {!Array.isArray(received) || hasNoChildren ? (
            <></>
          ) : (
            <Box minWidth={0} style={{ flex: 1 }}>
              {renderLogIcon(sumActionTypeCounts(received))}
            </Box>
          )}
        </Box>
        <Box
          classes={classNames("actionButtons", {
            ar: i18n.language.includes("ar"),
          })}
        >
          {log.logType.includes("document") &&
            workgroupInfo?.workgroupType !== "HUB" &&
            hasNoChildren && (
              <Button
                btnStyle={"table-icon"}
                size="sm"
                classes="mg ml-4"
                onClick={(e) => {
                  e.stopPropagation();
                  setSettingInfo &&
                    setSettingInfo({
                      x: dir === "ltr" ? e.pageX - 240 : e.pageX,
                      y: e.pageY - 61,
                      logId:
                        settingButtonInfo?.logId === logId
                          ? settingButtonInfo.logId
                          : "logIds" in log
                          ? log.logIds[0]
                          : "logId" in log
                          ? log.logId
                          : id,
                      log:
                        settingButtonInfo?.logId === logId
                          ? settingButtonInfo.log
                          : undefined,
                    });
                }}
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            )}
        </Box>
      </Box>
    );
  };

  /** run 카드 */
  const renderLogItem = (run: RunGroup, index: string) => {
    const hasNoChildren =
      (Array.isArray(run) &&
        run.length <= 1 &&
        (run[0]?.logIds?.length ?? 0) <= 1) ||
      (!Array.isArray(run) && !("children" in run) && !("logIds" in run));
    return (
      <Box
        classes="compressed-item"
        direction="column"
        width={"calc(100% - 10px)"}
        key={index}
      >
        <Box
          classes={classNames("compressed-item--icon", {
            clicked: checkboxGroup.isSelected(index),
            "cursor pointer": !hasNoChildren,
          })}
          onClick={() => (hasNoChildren ? undefined : handleGetLogBelow(index))}
          alignItems="center"
          justifyContent="center"
        />
        {renderLogEachItem(run, index, 1, !checkboxGroup.isSelected(index))}
      </Box>
    );
  };

  /** 렌더 */
  return (
    <Box
      direction="column"
      width={"100%"}
      id={"compressedLog"}
      classes="compressed"
      style={{ overflow: pageType === "workgroupLog" ? "auto" : "visible" }}
    >
      {logs && logs.filter((day) => day.length > 0).length > 0 ? (
        logs
          .filter((day) => day.length > 0)
          .map((dayLogs, dIdx) =>
            dayLogs.map((run, rIdx) => {
              const head = run[0];
              const last = run[run.length - 1];
              const index = `${utilsCommon.renderDateFormat(
                dayLogs[0][0].firstDate,
                "YYYY-MM-DD"
              )}--${rIdx}`;
              return (
                <Box
                  width={"100%"}
                  key={`day-${dIdx}-${rIdx}`}
                  direction="column"
                >
                  {rIdx === 0 && (
                    <Box width={"100%"} key={`day-${dIdx}-header`} height={32}>
                      <Box minWidth={122}>
                        <Typography type="subtitle1">
                          {utilsCommon.renderDateFormat(
                            dayLogs[0][0].firstDate,
                            (i18n.language.includes("ko") ? "" : "ddd, ") +
                              "MMM D" +
                              (i18n.language.includes("ko") ? "일(ddd)" : ""),
                            i18n.language
                          )}
                        </Typography>
                      </Box>
                      <Box
                        direction="column"
                        width={"calc(100% - 122px)"}
                        classes="compressed-item"
                      />
                    </Box>
                  )}
                  <Box width={"100%"} key={`day-${dIdx}-${rIdx}-body`}>
                    <Box minWidth={122} style={{ marginTop: 12 }}>
                      <Typography
                        type={
                          // pageType === "workgroupLog" ? "body2_rg" : "body3_rg"
                          "body3_rg"
                        }
                        textAlign="end"
                        style={{ minWidth: "100px" }}
                      >
                        {renderCompressedDateRange(
                          true,
                          head.logIds.length <= 1 && run.length === 1
                            ? head.firstDate
                            : last.lastDate,
                          head.firstDate
                        )}
                      </Typography>
                    </Box>
                    <Box
                      direction="column"
                      width={"calc(100% - 122px)"}
                      boxSizing="border-box"
                    >
                      {renderLogItem(run, index)}
                    </Box>
                  </Box>
                  {checkboxGroup.isSelected(index) &&
                    run.map((item, ridx) => {
                      const itemId = `${index}--${ridx}`;
                      return (
                        <Box direction="column" key={itemId}>
                          {item.childrenLogs?.map((item4, index4) => {
                            const leafId = `${itemId}--${index4}`;
                            return (
                              <Box>
                                <Box minWidth={122} style={{ marginTop: 12 }}>
                                  <Typography
                                    type="body3_rg"
                                    textAlign="end"
                                    width={"100px"}
                                    exactColor={getColor(
                                      "COLOR_TEXT_SECONDARY"
                                    )}
                                  >
                                    {renderCompressedDateRange(
                                      false,
                                      item4.logDate
                                    )}
                                  </Typography>
                                </Box>
                                <Box
                                  direction="column"
                                  key={leafId}
                                  width={"calc(100% - 122px)"}
                                  boxSizing="border-box"
                                  classes="compressed-item--body"
                                >
                                  <Box
                                    classes={"compressed-item--icon"}
                                    alignItems="center"
                                    justifyContent="center"
                                  />
                                  {renderLogEachItem(
                                    item4,
                                    leafId,
                                    2,
                                    index4 ===
                                      (item.childrenLogs?.length ?? 0) - 1
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                </Box>
              );
            })
          )
      ) : !dateFilter ||
        (dayjs().year() === parseInt(dateFilter.year) &&
          dayjs().month() === parseInt(dateFilter.month)) ? (
        <EmptyInitFilterPage
          pageType={
            pageType === "workgroupHome"
              ? "workgroup_home_log"
              : pageType === "home"
              ? "home_log"
              : "compressed_log"
          }
        />
      ) : (
        <EmptyFilterPage
          handleInitFilter={handleInitFilter}
          pageType={"compressed_log"}
        />
      )}
    </Box>
  );
};

export default CompressWorkgroupLog;
